import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { PMButton } from "../../../utils/PMButton";
import { PMDialog } from "../../../utils/PMDialog";
import { AddSubDialog } from "./add-sub-dialog";
import { CreateReportsDialog } from "./create-reports-dialog";
import axios from "../../../../api/axios";
import { toast } from "react-hot-toast";
import { mapErrors } from "../../../Utils";

export const ServiceAgreementSubs = ({ agreement, onRefresh }) => {
  const { t } = useTranslation();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addSubDialogOpen, setAddSubDialogOpen] = useState(false);
  const [createReportsDialogOpen, setCreateReportsDialogOpen] = useState(false);
  const [selectedSubs, setSelectedSubs] = useState([]);

  useEffect(() => {
    if (agreement?.id) {
      fetchSubs();
    }
  }, [agreement?.id]);

  const fetchSubs = async () => {
    if (!agreement?.id) {
      console.error("No agreement ID provided");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`/service/agreements/${agreement.id}/subs`);
      setSubs(response.data.data || []);
    } catch (error) {
      console.error("Error fetching subs:", error);
      toast.error("Failed to fetch subs");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSub = async (subData) => {
    if (!agreement?.id) {
      throw new Error("No agreement ID provided");
    }
    
    try {
      const response = await axios.post(`/service/agreements/${agreement.id}/subs`, {
        sub: subData
      });
      
      if (response.status === 201) {
        toast.success("Sub added successfully");
        setAddSubDialogOpen(false);
        fetchSubs();
        if (onRefresh) onRefresh(agreement);
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      console.error("Error adding sub:", error);
      const errorMessage = error.response?.data?.errors 
        ? mapErrors(error.response.data.errors)
        : error.response?.data?.message || error.message || "Failed to add sub";
      
      // Throw the error so the dialog can catch and display it
      throw new Error(errorMessage);
    }
  };

  const handleDeleteSub = async (subId) => {
    if (!agreement?.id) {
      toast.error("No agreement ID provided");
      return;
    }
    
    if (!window.confirm("Are you sure you want to delete this sub?")) {
      return;
    }

    try {
      const response = await axios.delete(`/service/agreements/${agreement.id}/subs/${subId}`);
      
      if (response.status === 200) {
        toast.success("Sub deleted successfully");
        fetchSubs();
        if (onRefresh) onRefresh(agreement);
      }
    } catch (error) {
      console.error("Error deleting sub:", error);
      toast.error("Failed to delete sub");
    }
  };

  const handleSubSelection = (subId, isSelected) => {
    if (isSelected) {
      setSelectedSubs(prev => [...prev, subId]);
    } else {
      setSelectedSubs(prev => prev.filter(id => id !== subId));
    }
  };

  const handleSelectAll = () => {
    if (selectedSubs.length === subs.length) {
      setSelectedSubs([]);
    } else {
      setSelectedSubs(subs.map(sub => sub.id));
    }
  };

  const handleCreateReports = async (reportSettings) => {
    if (!agreement?.id) {
      toast.error("No agreement ID provided");
      return;
    }
    
    try {
      const response = await axios.post(`/service/agreements/${agreement.id}/create_reports`, {
        sub_ids: selectedSubs,
        settings: reportSettings
      });
      
      if (response.status === 200) {
        toast.success(`Created ${response.data.reports_created} reports successfully`);
        setCreateReportsDialogOpen(false);
        setSelectedSubs([]);
        if (onRefresh) onRefresh(agreement);
      }
    } catch (error) {
      console.error("Error creating reports:", error);
      const errorMessage = error.response?.data?.errors 
        ? mapErrors(error.response.data.errors)
        : "Failed to create reports";
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if no agreement ID
  if (!agreement?.id) {
    return (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <div className="text-center text-gray-500">
            <p className="text-sm">No agreement selected</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <section className="mt-5" aria-labelledby="subs-information-title">
        <div className="bg-white shadow sm:rounded-lg">
          <div className="flex items-center justify-between px-4 py-5 sm:px-6">
            <div className="block w-[60%]">
              <h2
                id="subs-information-title"
                className="text-lg font-medium leading-6 text-gray-900"
              >
                Subs ({subs.length})
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Manage units and customers included in this service agreement
              </p>
            </div>
            <div className="flex gap-2">
              {subs.length > 0 && (
                <PMButton
                  onClick={() => setCreateReportsDialogOpen(true)}
                  disabled={selectedSubs.length === 0}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Opprett oppdrag ({selectedSubs.length})
                </PMButton>
              )}
              <PMButton
                onClick={() => setAddSubDialogOpen(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Sub
              </PMButton>
            </div>
          </div>
          
          {subs.length > 0 && (
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedSubs.length === subs.length && subs.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">Select All</span>
                </label>
              </div>
              
              <div className="space-y-4">
                {subs.map((sub) => (
                  <div
                    key={sub.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <input
                          type="checkbox"
                          checked={selectedSubs.includes(sub.id)}
                          onChange={(e) => handleSubSelection(sub.id, e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-4">
                            <div>
                              <h3 className="text-sm font-medium text-gray-900">
                                {sub.name || sub.customer?.name || "Unnamed Sub"}
                              </h3>
                              <div className="mt-1 text-sm text-gray-500">
                                {sub.description && (
                                  <p className="mb-1">{sub.description}</p>
                                )}
                                {sub.customer && (
                                  <p>Customer: {sub.customer.name}</p>
                                )}
                                {sub.system && (
                                  <p>System: {sub.system.displayTitle}</p>
                                )}
                                {sub.contact && (
                                  <p>Contact: {sub.contact.displayTitle}</p>
                                )}
                                {sub.delivery_address && (
                                  <p>Address: {sub.delivery_address.address}, {sub.delivery_address.postal} {sub.delivery_address.place}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <PMButton
                          onClick={() => handleDeleteSub(sub.id)}
                          className="bg-red-600 hover:bg-red-700 text-white p-2"
                          tooltip="Delete sub"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </PMButton>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {subs.length === 0 && (
            <div className="border-t border-gray-200 px-4 py-8 sm:px-6 text-center">
              <div className="text-gray-500">
                <p className="text-sm">No subs added yet.</p>
                <p className="text-xs mt-1">Click &quot;Add Sub&quot; to get started.</p>
              </div>
            </div>
          )}
        </div>
      </section>

      <AddSubDialog
        open={addSubDialogOpen}
        onClose={() => setAddSubDialogOpen(false)}
        onSubmit={handleAddSub}
        agreement={agreement}
      />

      <CreateReportsDialog
        open={createReportsDialogOpen}
        onClose={() => setCreateReportsDialogOpen(false)}
        onSubmit={handleCreateReports}
        selectedSubsCount={selectedSubs.length}
        agreement={agreement}
      />
    </>
  );
};
